import type { ImageVariable } from '@carboncopyco/types';
import { Button } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import type React from 'react';
import { useMemo, useState } from 'react';
import type { ImageDetails } from '../ImageInput';
import {
  DestinationImageField,
  type DestinationImageFieldProps,
} from './DestinationImageField';
import { DestinationSelection } from './DestinationSelection';
import { DestinationVariableField } from './DestinationVariableField';
import {
  SourceImageField,
  type SourceImageFieldProps,
} from './SourceImageField';

type Destination =
  | { destination: ImageDetails; destinationType: 'image' }
  | {
      destination: ImageVariable;
      destinationType: 'variable';
    };

type InitialValues =
  | {
      sourceImage: ImageDetails;
      destination?: never;
      destinationType?: never;
    }
  | ({
      sourceImage: ImageDetails;
    } & Destination);

export type ImageReplaceFormValues = {
  sourceImage: ImageDetails;
} & Destination;

export type ImageReplaceFormProps = Pick<
  SourceImageFieldProps,
  'errorMessage' | 'onLearnMoreClick'
> &
  Pick<DestinationImageFieldProps, 'autoFocus' | 'onFocus'> & {
    onSubmit: (values: ImageReplaceFormValues) => Promise<void>;
    submitText?: string;
    onCancel?: () => void;
    initialValues?: InitialValues;
    variables?: ImageVariable[];
  } & (
    | { disabled?: boolean; allowSubmission?: never }
    | { disabled?: never; allowSubmission?: boolean }
  );

export const ImageReplaceForm = ({
  initialValues,
  onSubmit,
  submitText,
  onCancel,
  disabled,
  errorMessage,
  onLearnMoreClick,
  autoFocus,
  onFocus,
  variables,
  allowSubmission,
}: ImageReplaceFormProps) => {
  const [submitting, setSubmitting] = useBoolean();
  const [sourceImageDetails, setSourceImageDetails] = useState<ImageDetails>();
  const [destinationImageDetails, setDestinationImageDetails] =
    useState<ImageDetails>(
      initialValues?.destinationType === 'image' && initialValues?.destination
    );
  const [destinationVariable, setDestinationVariable] = useState<ImageVariable>(
    initialValues?.destinationType === 'variable' && initialValues?.destination
  );

  const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting.setTrue();
    try {
      await onSubmit({
        sourceImage: sourceImageDetails,
        ...(destinationImageDetails && {
          destination: destinationImageDetails,
          destinationType: 'image',
        }),
        ...(destinationVariable && {
          destination: destinationVariable,
          destinationType: 'variable',
        }),
      });
      resetForm();
    } catch (e) {
      console.error('Failed to submit image replace form', e);
    }
    setSubmitting.setFalse();
  };

  const resetForm = () => {
    setSourceImageDetails(null);
    setDestinationImageDetails(null);
  };

  const isValid = useMemo(
    () =>
      sourceImageDetails &&
      (destinationImageDetails || destinationVariable) &&
      (initialValues.destinationType === 'image'
        ? destinationImageDetails.url !== initialValues.destination.url
        : true) &&
      (initialValues.destinationType === 'variable'
        ? destinationVariable.variableId !==
          initialValues.destination.variableId
        : true),
    [
      sourceImageDetails,
      destinationImageDetails,
      destinationVariable,
      initialValues,
    ]
  );

  return (
    <form
      className="flex flex-col gap-5"
      aria-label="Find and replace image"
      onSubmit={submitForm}
    >
      <div className="flex flex-col gap-4">
        <SourceImageField
          errorMessage={errorMessage}
          imageDetails={initialValues?.sourceImage}
          onChangeImageDetails={setSourceImageDetails}
          onLearnMoreClick={onLearnMoreClick}
        />
        {!destinationImageDetails && !destinationVariable && (
          <DestinationSelection
            variables={variables}
            autoFocus={autoFocus}
            onFocus={onFocus}
            onSelectedImage={setDestinationImageDetails}
            onSelectedVariable={setDestinationVariable}
          />
        )}
        {destinationImageDetails && (
          <DestinationImageField
            autoFocus={autoFocus}
            onFocus={onFocus}
            imageDetails={destinationImageDetails}
            onChangeImageDetails={setDestinationImageDetails}
          />
        )}
        {destinationVariable && (
          <DestinationVariableField
            autoFocus={autoFocus}
            onFocus={onFocus}
            variable={destinationVariable}
            onChangeVariable={setDestinationVariable}
            variables={variables}
          />
        )}
      </div>
      <div className="flex justify-end">
        <div className="flex flex-row gap-2.5">
          <Button
            label="Cancel"
            onClick={() => onCancel?.()}
            highContrast
          />
          <Button
            aria-label="Submit"
            label={submitText ?? 'Submit'}
            type="submit"
            disabled={(disabled || submitting || !isValid) && !allowSubmission}
          />
        </div>
      </div>
    </form>
  );
};
